import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, tap } from 'rxjs';

import { ApiService } from '../api/api.service';
import {
  AuthSession,
  ChangePasswordRequest,
  GoogleLoginRequest,
  LoginRequest,
  LoginResponse,
  PasswordResetRequest,
  PasswordResetTokenValidationRequest,
  PasswordResetTokenValidationResponse,
  ResetPasswordRequest,
} from '../models/auth.model';
import { Permission } from '../models/permission.model';
import { GoogleIdentityService } from '../services/google-identity.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly tokenStorageKey = 'lexium_internal_token';
  private readonly userStorageKey = 'lexium_internal_user';

  readonly session = signal<AuthSession | null>(this.readSession());

  constructor(
    private readonly api: ApiService,
    private readonly router: Router,
    private readonly googleIdentity: GoogleIdentityService
  ) {}

  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.api.post<Record<string, unknown>>('/auth/login', payload).pipe(
      map((response) => this.normalizeLoginResponse(response)),
      tap((response) => this.persistSession(response))
    );
  }

  loginWithGoogle(idToken: string): Observable<LoginResponse> {
    const payload: GoogleLoginRequest = { id_token: idToken };
    return this.api.post<Record<string, unknown>>('/auth/google', payload).pipe(
      map((response) => this.normalizeLoginResponse(response)),
      tap((response) => this.persistSession(response))
    );
  }

  me(): Observable<unknown> {
    return this.api.get('/me');
  }

  requestPasswordReset(payload: PasswordResetRequest): Observable<unknown> {
    return this.api.post('/auth/request-password-reset', payload);
  }

  validatePasswordResetToken(
    payload: PasswordResetTokenValidationRequest
  ): Observable<PasswordResetTokenValidationResponse> {
    return this.api.post<Record<string, unknown>>('/auth/validate-password-reset-token', payload).pipe(
      map((response) => {
        const data = this.extractPayload(response);
        return {
          valid: Boolean(data['valid']),
          purpose:
            data['purpose'] === 'INVITE' || data['purpose'] === 'RESET_PASSWORD'
              ? (data['purpose'] as 'INVITE' | 'RESET_PASSWORD')
              : undefined,
        };
      })
    );
  }

  resetPassword(payload: ResetPasswordRequest): Observable<unknown> {
    return this.api.post('/auth/reset-password', payload);
  }

  changePassword(payload: ChangePasswordRequest): Observable<{ token: string; token_type: string }> {
    return this.api.post<Record<string, unknown>>('/auth/change-password', payload).pipe(
      map((response) => this.normalizeTokenRefreshResponse(response)),
      tap((response) => this.persistTokenOnly(response.token))
    );
  }

  getToken(): string | null {
    return this.session()?.token ?? null;
  }

  isAuthenticated(): boolean {
    return Boolean(this.getToken());
  }

  /** Verifica se o usuário autenticado é administrador. */
  isAdmin(): boolean {
    const user = this.session()?.user as Record<string, unknown> | null;
    return user?.['is_admin'] === true;
  }

  hasPermission(permissionName: string): boolean {
    const user = this.session()?.user as (Record<string, unknown> & { roles?: unknown; permissions?: unknown }) | null;

    if (!user) {
      return false;
    }

    // Admin tem acesso irrestrito — espelha a lógica do PermissionFilter no backend
    if ((user as Record<string, unknown>)['is_admin'] === true) {
      return true;
    }

    const directPermissions = Array.isArray(user.permissions)
      ? user.permissions.filter((item): item is string => typeof item === 'string')
      : [];

    if (directPermissions.includes(permissionName)) {
      return true;
    }

    const roles = Array.isArray(user.roles) ? user.roles : [];

    return roles.some((role) => {
      if (role && typeof role === 'object' && 'permissions' in role) {
        const permissions = (role as { permissions?: unknown }).permissions;
        if (!Array.isArray(permissions)) {
          return false;
        }

        return permissions.some((permission) => {
          if (typeof permission === 'string') {
            return permission === permissionName;
          }

          return typeof permission === 'object' && permission !== null && (permission as Permission).name === permissionName;
        });
      }

      return false;
    });
  }

  /** Verifica se o usuário possui pelo menos uma das permissões fornecidas. */
  hasAnyPermission(permissionNames: string[]): boolean {
    return permissionNames.some((p) => this.hasPermission(p));
  }

  clearSession(): void {
    localStorage.removeItem(this.tokenStorageKey);
    localStorage.removeItem(this.userStorageKey);
    this.session.set(null);
  }

  logout(): void {
    this.googleIdentity.signOut();
    this.clearSession();
    void this.router.navigateByUrl('/login');
  }

  private readSession(): AuthSession | null {
    const token = localStorage.getItem(this.tokenStorageKey);
    const userRaw = localStorage.getItem(this.userStorageKey);

    if (!token) {
      return null;
    }

    return {
      token,
      user: userRaw ? (JSON.parse(userRaw) as AuthSession['user']) : null
    };
  }

  private normalizeLoginResponse(response: Record<string, unknown>): LoginResponse {
    const payload = this.extractPayload(response);
    const token = (payload['token'] as string | undefined) ?? (payload['access_token'] as string | undefined);

    if (!token) {
      throw new Error('Token de autenticação não foi retornado pelo backend.');
    }

    const user = payload['user'] as LoginResponse['user'];
    return { token, user };
  }

  private normalizeTokenRefreshResponse(response: Record<string, unknown>): { token: string; token_type: string } {
    const payload = this.extractPayload(response);
    const token = (payload['token'] as string | undefined) ?? '';
    const tokenType = (payload['token_type'] as string | undefined) ?? 'internal';

    if (!token) {
      throw new Error('Token atualizado não foi retornado pelo backend.');
    }

    return { token, token_type: tokenType };
  }

  private extractPayload(response: Record<string, unknown>): Record<string, unknown> {
    const data = response['data'];

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, unknown>;
    }

    return response;
  }

  private persistSession(loginResponse: LoginResponse): void {
    localStorage.setItem(this.tokenStorageKey, loginResponse.token);

    if (loginResponse.user) {
      localStorage.setItem(this.userStorageKey, JSON.stringify(loginResponse.user));
    }

    this.session.set({
      token: loginResponse.token,
      user: loginResponse.user ?? null
    });
  }

  private persistTokenOnly(token: string): void {
    const current = this.session();
    localStorage.setItem(this.tokenStorageKey, token);
    this.session.set({
      token,
      user: current?.user ?? null,
    });
  }
}
